import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MojoHasFeatureExtensionsGuard } from 'mojo-feature-extensions';
import { MojoHasAnyPrivilegeGuard, MojoHasPrivilegesGuard } from 'mojo-privileges';

import { InitialDataGuard } from '@core';
import { FeatureAccessGuard } from '@core/guards';
import { AuthGuard } from '@core/auth';
import { MobileGlobalSearchModalComponent } from '@layout/components/mobile-global-search-modal/mobile-global-search-modal.component';

import { appConfig } from './app.config';
import { AppResolver } from './app.resolver';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { TalentPoolFormGuard } from '@modules/talent-pools-page/guards/talent-pool-form.guard';
import { PBINavBlockGuard } from './modules/pbi-report/guards/pbi-nav-block.guard';

const routerTalentPools = () =>
  import('@modules/talent-pools-page/talent-pools.module').then(
    (module) => module.TalentPoolsModule,
  );

export const appRoutes: Routes = [
  {
    path: '',
    canActivateChild: [AuthGuard, InitialDataGuard],
    resolve: [AppResolver],
    children: [
      {
        path: 'account',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.settings.profile],
        },
        loadChildren: () =>
          import('./modules/account/account.module').then((module) => module.AccountModule),
      },
      {
        path: 'candidates',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.candidates.view],
        },
        loadChildren: () =>
          import('./modules/candidates/candidates.module').then(
            (module) => module.CandidatesModule,
          ),
      },
      {
        path: 'landing',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.candidates.view],
        },
        loadChildren: () =>
          import('./modules/landing-page/landing.module').then((module) => module.LandingModule),
      },
      {
        path: 'jobs',
        canActivate: [PBINavBlockGuard],
        loadChildren: () =>
          import('./modules/jobs-page/jobs.module').then((module) => module.JobsModule),
      },
      {
        path: 'metrics',
        loadChildren: () =>
          import('./modules/metrics/metrics.module').then((module) => module.MetricsModule),
      },
      {
        path: 'companies',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.companies.view],
        },
        loadChildren: () =>
          import('./modules/companies/companies.module').then((module) => module.CompaniesModule),
      },
      {
        path: 'reports',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.reports.view],
        },
        loadChildren: () =>
          import('@modules/reports/reports.module').then((module) => module.ReportsModule),
      },
      {
        path: 'new-reports',
        canActivate: [FeatureAccessGuard],
        canLoad: [FeatureAccessGuard],
        data: {
          featureAccessAny: [
            AppFeatureExtensions.CONVERSION_AND_ROI_REPORT,
            AppFeatureExtensions.USAGE_REPORT,
            AppFeatureExtensions.PROJECTS_REPORT,
          ],
        },
        loadChildren: () =>
          import('@modules/new-reports/new-reports.module').then(
            (module) => module.NewReportsModule,
          ),
      },
      {
        path: 'talent-pools',
        canActivate: [PBINavBlockGuard],
        canLoad: [MojoHasFeatureExtensionsGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.talentPools.view],
          featureExtension: [AppFeatureExtensions.TALENT_POOLS],
        },
        loadChildren: routerTalentPools,
      },
      {
        path: 'api-keys',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          featureExtension: [AppFeatureExtensions.API_KEY],
        },
        loadChildren: () =>
          import('./modules/api-management/api-management.module').then(
            (module) => module.ApiManagementModule,
          ),
      },
      {
        path: 'users',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.settings.user],
        },
        loadChildren: () =>
          import('./modules/users/users.module').then((module) => module.UsersModule),
      },
      {
        path: 'job-boards',
        canActivate: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.jobBoards.viewPage],
        },
        loadChildren: () =>
          import('./modules/job-board/job-board.module').then((module) => module.JobBoardModule),
      },
      {
        path: 'settings',
        canActivate: [PBINavBlockGuard],
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.settings.anonymization],
        },
        loadChildren: () =>
          import('./modules/settings/settings.module').then((module) => module.SettingsModule),
      },
      {
        path: 'style-guide',
        loadChildren: () =>
          import('./styleguide/style-guide.module').then((module) => module.StyleGuideModule),
      },
      {
        path: 'pbi-report',
        canActivate: [PBINavBlockGuard],
        canLoad: [MojoHasFeatureExtensionsGuard],
        data: {
          featureExtension: [AppFeatureExtensions.IRP_PBI_REPORT],
        },
        loadChildren: () =>
          import('./modules/pbi-report/pbi-report.module').then((module) => module.PBIReportModule),
      },
      { path: '**', redirectTo: 'jobs', pathMatch: 'full' },
    ],
  },
  {
    outlet: 'modal',
    path: '',
    children: [
      {
        path: 'talent-pools-new-candidate',
        data: {
          excludedFeatureExtension: AppFeatureExtensions.POOLS_SYNCHRONIZATION,
        },
        canActivate: [TalentPoolFormGuard],
        loadChildren: routerTalentPools,
      },
      {
        path: 'global-search',
        component: MobileGlobalSearchModalComponent,
      },
      {
        path: 'candidateDetails',
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.candidates.edit],
        },
        loadChildren: () =>
          import('./modules/candidates/+details/candidate-details.module').then(
            (module) => module.CandidateDetailsModule,
          ),
      },
      {
        path: 'candidateForm',
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.candidates.edit],
        },
        loadChildren: () =>
          import('./modules/candidates/+form/candidate-form.module').then(
            (module) => module.CandidateFormModule,
          ),
      },
      {
        path: 'candidate',
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.candidates.create],
        },
        loadChildren: () =>
          import('./modules/candidates/+upload-assign/candidate-upload.module').then(
            (module) => module.CandidateUploadModule,
          ),
      },
      {
        path: 'candidate-v2',

        loadChildren: () =>
          import('./modules/candidates-form/candidates-form.module').then(
            (module) => module.CandidatesFormModule,
          ),
      },
      {
        path: 'merge-candidate',
        loadChildren: () =>
          import('./modules/candidates/merge-candidate/merge-candidate.module').then(
            (module) => module.MergeCandidateModule,
          ),
      },
      {
        path: 'candidates-attachments',
        loadChildren: () =>
          import('./modules/candidates/+attachments/+upload/upload.module').then(
            (module) => module.CandidateAttachmentsUploadModule,
          ),
      },
      {
        path: 'job',
        canLoad: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.job.create],
        },
        loadChildren: () =>
          import('./shared/job-form/job-form.module').then((module) => module.JobsFormModule),
      },
      {
        path: 'user',
        canLoad: [AuthGuard],
        loadChildren: () =>
          import('./modules/user-form/user-form.module').then((module) => module.UserFormModule),
      },
      {
        path: 'job-publishing',
        canActivate: [AuthGuard, MojoHasPrivilegesGuard],
        data: {
          privileges: [appConfig.privileges.job.publish],
        },
        loadChildren: () =>
          import('./modules/job-page/modals/job-publishing/job-publishing.module').then(
            (module) => module.JobPublishingModule,
          ),
      },
      {
        path: 'team-members',
        canLoad: [AuthGuard, MojoHasAnyPrivilegeGuard],
        data: {
          privileges: [
            appConfig.privileges.job.teamMembers.view,
            appConfig.privileges.talentPools.create,
          ],
        },
        loadChildren: () =>
          import('./shared/modal/team-members/team-members-modal.module').then(
            (module) => module.TeamMembersModalModule,
          ),
      },
      {
        path: 'filter-companies',
        loadChildren: () =>
          import('./modules/companies/filter/filter.module').then(
            (module) => module.CompaniesFilterModule,
          ),
      },
    ],
  },
];

// TODO: Implement Page Not Found
// { path: '**', component: PageNotFoundComponent }

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: 'enabled',
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload',
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
  providers: [AppResolver],
})
export class AppRoutingModule {}
